<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { reactive, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import { ComponentExposed } from 'vue-component-type-helpers'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import TextField from '@/components/input/TextField.vue'
import StepTypePicker from '@/components/input/StepTypePicker.vue'
import { normalName, required } from '@/validation'
import useCreateStepNode, { CreatedStepNode } from '@/composables/useCreateStepNode'

const props = defineProps<{
  productId: string
  moduleId: string
}>()

const { t } = useI18n()
const { createStepNode } = useCreateStepNode()

type Model = { name?: string; stepTypeId?: string }
const model = reactive<Model>({})
const validation = useVuelidate<Model>(
  { name: { required, normalName }, stepTypeId: { required } },
  model,
)

const dialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const open = async (): Promise<CreatedStepNode | undefined> => {
  model.name = undefined
  model.stepTypeId = undefined

  let created: CreatedStepNode | undefined
  await dialog.value?.open(async () => {
    created = await createStepNode({
      productId: props.productId,
      moduleId: props.moduleId,
      name: model.name as string,
      stepTypeId: model.stepTypeId as string,
    })
  })
  return created
}

defineExpose({ open })
</script>

<template>
  <upsert-dialog
    ref="dialog"
    type="create"
    :entity="t('entity.stepSection.singular')"
    :validation="validation"
  >
    <p class="text-medium-emphasis text-body-2 mb-4">
      {{ t('component.createSectionDialog.hint') }}
    </p>
    <text-field
      v-model="model.name"
      :label="t('entity.step.field.name')"
      :validation="validation.name"
      required
    />
    <step-type-picker
      v-model="model.stepTypeId"
      :label="t('entity.step.field.stepType')"
      :validation="validation.stepTypeId"
      required
    />
  </upsert-dialog>
</template>

<style scoped lang="scss"></style>
